import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatNumber } from 'utils/number';
import styles from './color-picker.module.scss';

export const DEFAULT_COLORS = [
  '#4589ff',
  '#42be65',
  '#da1e28',
  '#8a3ffc',
  '#b28600',
  '#009d9a',
  '#fa4d56',
  '#0f62fe',
  '#198038',
  '#a56eff',
  '#f1c21b',
  '#08bdba',
  '#78a9ff',
  '#6fdc8c',
  '#ff8389',
  '#be95ff',
  '#fddc69',
  '#3ddbd9',
  '#161616',
  '#525252',
  '#a6c8ff',
  '#a7f0ba',
  '#ffb3b8',
  '#d4bbff',
];

function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function parsePickerColor(color: string): { hex: string; opacity: number } {
  if (/^#[0-9a-f]{6}$/i.test(color)) return { hex: color, opacity: 100 };
  if (/^#[0-9a-f]{8}$/i.test(color)) {
    const alpha = parseInt(color.slice(7, 9), 16);
    return { hex: color.slice(0, 7), opacity: Math.round((alpha / 255) * 100) };
  }
  const rgba = color.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (rgba) {
    const r = clamp(Math.round(Number(rgba[1])), 0, 255);
    const g = clamp(Math.round(Number(rgba[2])), 0, 255);
    const b = clamp(Math.round(Number(rgba[3])), 0, 255);
    const a = rgba[4] === undefined ? 1 : clamp(Number(rgba[4]), 0, 1);
    const hex = `#${[r, g, b]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')}`;
    return { hex, opacity: Math.round(a * 100) };
  }
  return { hex: '#4589ff', opacity: 100 };
}

export function hexToRgba(hex: string, opacity: number): string {
  const parsed = parsePickerColor(hex);
  const h = parsed.hex;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  const a = clamp(opacity, 0, 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${formatNumber(a, 2, 2, 'decimal', false)})`;
}

const SaturationCanvas: React.FC<{
  hue: number;
  sat: number;
  val: number;
  onSatValChange: (s: number, v: number) => void;
  onSatValChangeEnd: (s: number, v: number) => void;
}> = ({ hue, sat, val, onSatValChange, onSatValChangeEnd }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragging = useRef(false);
  const onChangeRef = useRef(onSatValChange);
  const onEndRef = useRef(onSatValChangeEnd);
  onChangeRef.current = onSatValChange;
  onEndRef.current = onSatValChangeEnd;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = canvas;
    ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
    ctx.fillRect(0, 0, width, height);
    const white = ctx.createLinearGradient(0, 0, width, 0);
    white.addColorStop(0, 'rgba(255,255,255,1)');
    white.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = white;
    ctx.fillRect(0, 0, width, height);
    const black = ctx.createLinearGradient(0, 0, 0, height);
    black.addColorStop(0, 'rgba(0,0,0,0)');
    black.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = black;
    ctx.fillRect(0, 0, width, height);
  }, [hue]);

  const pickCoords = (e: React.MouseEvent | MouseEvent): [number, number] => {
    const canvas = canvasRef.current;
    if (!canvas) return [sat, val];
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return [x, 1 - y];
  };

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const [s, v] = pickCoords(e);
    onChangeRef.current(s, v);
    const onMove = (me: MouseEvent) => {
      if (!dragging.current) return;
      const [ms, mv] = pickCoords(me);
      onChangeRef.current(ms, mv);
    };
    const onUp = (me: MouseEvent) => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const [es, ev] = pickCoords(me);
      onEndRef.current(es, ev);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className={styles.saturationWrapper}>
      <canvas
        ref={canvasRef}
        width={252}
        height={180}
        className={styles.saturationCanvas}
        onMouseDown={onMouseDown}
      />
      <div
        className={styles.saturationPointer}
        style={{ left: `${sat * 100}%`, top: `${(1 - val) * 100}%` }}
      />
    </div>
  );
};

// Approx rendered height of the picker popover (canvas + sliders + hex row);
// used by 'auto' placement to decide whether to open upward.
const POPOVER_HEIGHT_PX = 300;

/**
 * How many pickers are open right now.
 *
 * A picker's popover floats over whatever is behind it, and it closes on mousedown —
 * so the click that closes it still reaches what is underneath. On a page that is
 * mostly panels nobody notices; over a map, that click draws. A surface with
 * something to lose can ask whether a picker was open when the gesture started and
 * treat the click as the dismissal it was.
 */
let openPickers = 0;

/**
 * Whether any colour picker is currently open.
 *
 * @return {boolean} True while at least one picker's popover is showing.
 */
export const isColorPickerOpen = (): boolean => openPickers > 0;

export const ColorSwatch: React.FC<{
  color: string;
  opacity?: number;
  onChange: (color: string, opacity: number) => void;
  popoverPosition?: 'top' | 'bottom' | 'auto';
}> = ({
  color,
  opacity: opacityProp = 100,
  onChange,
  popoverPosition = 'bottom',
}) => {
  const parsedInitial = parsePickerColor(color);
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(parsedInitial.hex);
  const [opacity, setOpacity] = useState(
    opacityProp === 100 ? parsedInitial.opacity : opacityProp
  );
  const [hsv, setHsv] = useState<[number, number, number]>(() =>
    hexToHsv(parsedInitial.hex)
  );
  const swatchBtnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const latestHex = useRef(color);
  const latestOpacity = useRef(opacityProp);

  useEffect(() => {
    if (open) return;
    const parsed = parsePickerColor(color);
    setHexInput(parsed.hex);
    setHsv(hexToHsv(parsed.hex));
    setOpacity(opacityProp === 100 ? parsed.opacity : opacityProp);
    latestHex.current = parsed.hex;
    latestOpacity.current = opacityProp === 100 ? parsed.opacity : opacityProp;
  }, [color, open, opacityProp]);

  // One count for every open picker, kept here rather than in each caller: the
  // popover is what overlaps other surfaces, so it is what has to announce itself.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    openPickers += 1;
    return () => {
      openPickers -= 1;
    };
  }, [open]);

  // Close on click outside (check both swatch button and portal popover)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        swatchBtnRef.current &&
        !swatchBtnRef.current.contains(target) &&
        (!popoverRef.current || !popoverRef.current.contains(target))
      ) {
        setOpen(false);
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [open]);

  const updateLocalHsv = (h: number, s: number, v: number) => {
    const hex = hsvToHex(h, s, v);
    setHsv([h, s, v]);
    setHexInput(hex);
    latestHex.current = hex;
  };

  const commit = () => {
    onChange(latestHex.current, latestOpacity.current);
  };

  const handleHexChange = (val: string) => {
    setHexInput(val);
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      setHsv(hexToHsv(val));
      latestHex.current = val;
      onChange(val, latestOpacity.current);
    }
  };

  const handleOpacityChange = (val: number) => {
    setOpacity(val);
    latestOpacity.current = val;
  };

  // Compute fixed position from the swatch button's bounding rect
  const getPopoverStyle = useCallback((): React.CSSProperties => {
    if (!swatchBtnRef.current) return {};
    const rect = swatchBtnRef.current.getBoundingClientRect();
    // 'auto' flips the popover above the swatch when there's no room below but
    // there is above (e.g. a swatch low in the sidebar).
    const openUp =
      popoverPosition === 'top' ||
      (popoverPosition === 'auto' &&
        rect.bottom + POPOVER_HEIGHT_PX > window.innerHeight &&
        rect.top > POPOVER_HEIGHT_PX);
    if (openUp) {
      return {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
      };
    }
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
    };
  }, [popoverPosition]);

  const popoverContent = open
    ? createPortal(
        <div
          ref={popoverRef}
          className={styles.colorPopoverFixed}
          style={getPopoverStyle()}
        >
          <SaturationCanvas
            hue={hsv[0]}
            sat={hsv[1]}
            val={hsv[2]}
            onSatValChange={(s, v) => updateLocalHsv(hsv[0], s, v)}
            onSatValChangeEnd={(s, v) => {
              updateLocalHsv(hsv[0], s, v);
              commit();
            }}
          />
          <div className={styles.hueRow}>
            <input
              type="range"
              min={0}
              max={360}
              value={hsv[0]}
              onChange={(e) =>
                updateLocalHsv(Number(e.target.value), hsv[1], hsv[2])
              }
              onMouseUp={() => commit()}
              className={styles.hueSlider}
            />
          </div>
          <div className={styles.opacityRow}>
            <div className={styles.opacityTrack}>
              <div
                className={styles.opacityGradient}
                style={{
                  background: `linear-gradient(to right, transparent, ${hexInput})`,
                }}
              />
              <input
                type="range"
                min={0}
                max={100}
                value={opacity}
                onChange={(e) => handleOpacityChange(Number(e.target.value))}
                onMouseUp={() => commit()}
                className={styles.opacitySlider}
              />
            </div>
          </div>
          <div className={styles.hexRow}>
            <span className={styles.hexLabel}>HEX</span>
            <input
              className={styles.hexInput}
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              spellCheck={false}
            />
            <input
              className={styles.opacityInput}
              type="number"
              min={0}
              max={100}
              value={opacity}
              onChange={(e) =>
                handleOpacityChange(
                  Math.min(100, Math.max(0, Number(e.target.value)))
                )
              }
              onBlur={() => commit()}
            />
            <span className={styles.opacityPercent}>%</span>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={styles.swatchWrapper}>
      <button
        ref={swatchBtnRef}
        className={styles.swatch}
        style={{ backgroundColor: hexToRgba(hexInput, opacity) }}
        onClick={() => setOpen(!open)}
        type="button"
        aria-label="Pick color"
      />
      {popoverContent}
    </div>
  );
};
